import { useEffect, useState } from 'react';
import { Loader2, ShieldOff, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../../lib/adminApi';

export default function AdminReports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');

  async function load() {
    setLoading(true);
    try {
      const data = await adminApi(`/admin/reports?status=${filter}`);
      setReports(data.reports || []);
    } catch (err) {
      toast.error(err.message || 'Failed');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  async function resolve(reportId, action) {
    try {
      await adminApi(`/admin/reports/${reportId}/resolve`, {
        method: 'POST',
        body: JSON.stringify({ action }),
      });
      toast.success(`Report ${action}d`);
      load();
    } catch (err) {
      toast.error(err.message || 'Failed');
    }
  }

  return (
    <div className="p-8" data-testid="admin-reports-page">
      <h1 className="text-3xl font-serif font-bold text-white mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>Reports</h1>
      <p className="text-white/50 text-sm mb-5">User-submitted reports. Review and take action — affects the user across web and mobile.</p>

      <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="admin-reports-filter" className="bg-[#1A1820] border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white mb-4">
        <option value="pending">Pending</option>
        <option value="resolved">Resolved</option>
        <option value="dismissed">Dismissed</option>
        <option value="all">All</option>
      </select>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#C8553D]" /></div>
      ) : reports.length === 0 ? (
        <div className="text-center py-16 bg-[#1A1820] border border-white/10 rounded-2xl text-white/50">No reports</div>
      ) : (
        <ul className="space-y-3" data-testid="admin-reports-list">
          {reports.map((r) => (
            <li key={r.id} data-testid={`admin-report-${r.id}`} className="bg-[#1A1820] border border-white/10 rounded-2xl p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="px-2 py-0.5 bg-[#E76F51]/30 text-[#E76F51] text-[9px] font-bold tracking-wider rounded">{r.category?.toUpperCase()}</span>
                    <span className="text-xs text-white/40">{new Date(r.created_at).toLocaleString('en-CA', { dateStyle: 'short', timeStyle: 'short' })}</span>
                  </div>
                  <p className="text-sm text-white/80 mb-2">{r.description || <em className="text-white/40">No description provided</em>}</p>
                  <div className="flex flex-wrap gap-3 text-xs text-white/60">
                    <span><strong className="text-white/40">Reporter:</strong> {r.reporter?.name || r.reporter_id?.slice(0, 8)}</span>
                    <span><strong className="text-white/40">Reported:</strong> {r.reported?.name || r.reported_user_id?.slice(0, 8)}</span>
                    {r.reported?.report_count !== undefined && <span><strong className="text-white/40">Total reports against user:</strong> {r.reported.report_count}</span>}
                  </div>
                </div>
                {r.status === 'pending' && (
                  <div className="flex flex-col gap-1.5">
                    <button onClick={() => resolve(r.id, 'ban')} data-testid={`admin-report-ban-${r.id}`} className="flex items-center gap-1 px-3 py-1.5 bg-[#E76F51]/20 hover:bg-[#E76F51]/40 text-[#E76F51] font-bold rounded text-xs"><ShieldOff className="w-3.5 h-3.5" /> Ban User</button>
                    <button onClick={() => resolve(r.id, 'dismiss')} data-testid={`admin-report-dismiss-${r.id}`} className="flex items-center gap-1 px-3 py-1.5 bg-white/5 hover:bg-white/10 text-white/60 font-bold rounded text-xs"><Eye className="w-3.5 h-3.5" /> Dismiss</button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
